<template>
  <div class="cf-node-properties__field-sub-group clearfix">
    <template v-for="(param, index) in params">
      <div
        v-if="!param.group"
        :key="index"
        class="cf-node-properties__field"
        :class="getFieldSize(param.name)"
      >
        <label class="control-label">
          {{ param.displayName }} <span v-if="!param.isOptional">*</span>
          <i
            v-tooltip.top="param.description"
            class="fa fa-question-circle m-l-5"
            aria-hidden="true"
          />
        </label>
        <InputNumber
          v-if="param.type === 'number'"
          v-model="settings[parentSetting][param.name]"
          :placeholder="param.defaultValue"
          :required="!param.isOptional"
          :error="getFieldError(errors, param.name)"
          @input="handleSettingsChange"
        />
      </div>
    </template>

    <MultipleToggle
      v-if="!settings.voiceResponse"
      :value="selectedWhisperType"
      :options="whisperTypeOptions"
      class="cf-node-properties__field-btn-group col-md-6 p-l-0 p-r-0"
      @onToggle="whisperTypeChange($event, 'selectedWhisperType')"
    />

    <template v-if="settings.voiceResponse">
      <div class="p-t-22">
        <label class="btn btn-primary active col-md-3 p-l-0 p-r-0">
          {{ $t('AUDIO_COMPONENT_SAY') }}
        </label>
      </div>
    </template>

    <template v-for="(t, index) in whisperTypeParameters">
      <div
        v-if="t.name === selectedWhisperType"
        :key="`${t.name}-${index}`"
      >
        <div
          v-for="(p, parameterIndex) in t.parameters.filter(p => !p.isHidden).sort((a,b) => sayInputOrder.indexOf(a.displayName) - sayInputOrder.indexOf(b.displayName))"
          :key="`param-${index}-${parameterIndex}`"
          class="cf-node-properties__field"
          :class="getFieldSize(p.name, ivrVoicesFeatureFlag)"
        >
          <label class="control-label">
            {{ p.displayName === 'Text' && ivrVoicesFeatureFlag ? $t('PROMPT_MESSAGE') : p.displayName }} <span v-if="!p.isOptional">*</span>
            <i
              v-tooltip.top="p.description"
              class="fa fa-question-circle m-l-5"
              aria-hidden="true"
            />
          </label>

          <InputSearchableSelect
            v-if="p.name === 'URL'"
            v-model="settings[parentSetting][t.name][p.name]"
            :config="assetOptions"
            :required="!p.isOptional"
            class="file-upload-input flex--grow"
            :class="{'is-selected': settings[parentSetting][t.name][p.name]}"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleSettingsChange"
          >
            <InputFile
              v-tooltip="$t('DIAL_NODE_UPLOAD_AUDIO')"
              accept=".mp3,.wav"
              :show-file-name="false"
              @change="uploadAudioFile"
            />
            <RecordingButton
              v-if="settings[parentSetting][t.name][p.name]" 
              :recording-url="settings[parentSetting][t.name][p.name]"
              class="p-t-6 p-l-12"
              no-float
            />
          </InputSearchableSelect>

          <InputSelect
            v-else-if="p.name === 'language'"
            v-model="settings[parentSetting][t.name][p.name]"
            :options="ivrVoicesFeatureFlag && settings.voiceResponse ? newLanguageOptions : legacyLanguageOptions"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleLanguageChange"
          />

          <InputTextarea
            v-else-if="p.name === 'text'"
            v-model="settings[parentSetting][t.name][p.name]"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleVoiceTextChange"
          >
            <RecordingButton
              v-show="ivrVoicesFeatureFlag && !isLegacyVoiceSelected"
              ref="recordingButton"
              :recording-url="newTextToSpeechRecordingUrl"
              class="p-t-6"
              :icon-button="false"
              no-float
              :disabled="!settings[parentSetting][t.name][p.name] || recordingIsLoading || !newTextToSpeechRecordingUrl"
            />
          </InputTextarea>
          
          <InputSelect
            v-else-if="p.name === 'voice' && !ivrVoicesFeatureFlag"
            v-model="settings[parentSetting][t.name][p.name]"
            :options="ivrVoicesFeatureFlag && availableVoicesList ? availableVoicesList : p.values"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleVoiceChange"
          />

          <InputVoiceSelector
            v-else-if="p.name === 'voice' && ivrVoicesFeatureFlag"
            v-model="settings[parentSetting][t.name][p.name]"
            :voices="filterVoices()"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleVoiceChange"
          />

          <InputNumber
            v-else-if="p.type === 'number'"
            v-model="settings[parentSetting][t.name][p.name]"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleSettingsChange"
          />

          <InputText
            v-else-if="p.type === 'string'"
            v-model="settings[parentSetting][t.name][p.name]"
            :required="!p.isOptional"
            :error="getFieldError(errors, `${t.name}.${p.name}`)"
            @input="handleSettingsChange"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import InputFile from '@/core/components/Form/InputFile';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import RecordingButton from '@/modules/viewAssets/components/RecordingButton';
import { getFieldError, getFieldSize } from '../../../helpers/node';
import messages from '../../../locales';
import InputNumber from './Form/InputNumber';
import InputSearchableSelect from './Form/InputSearchableSelect';
import InputSelect from './Form/InputSelect';
import InputText from './Form/InputText';
import InputTextarea from './Form/InputTextarea';
import InputVoiceSelector from './Form/InputVoiceSelector.vue';

import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS } from '@/store/helpers';
import debounce from 'lodash/debounce';
import { mapActions, mapGetters, mapState } from 'vuex';

const assetsConfig = {
  reduce: (option) => option.publicUrl,
  label: 'originalFileName',
};

const sayInputOrder = ['Language', 'Voice', 'Text'];

const legacyLanguageOptions = [
  { name: 'Dutch', value: 'nl-NL' },
  { name: 'English (AU)', value: 'en-AU' },
  { name: 'English (GB)', value: 'en-GB' },
  { name: 'English (US)', value: 'en-US' },
  { name: 'French (France)', value: 'fr-FR' },
  { name: 'French (Canada)', value: 'fr-CA' },
  { name: 'German', value: 'de-DE' },
  { name: 'Italian', value: 'it-IT' },
  { name: 'Polish', value: 'pl-PL' },
  { name: 'Portuguese (Portugal)', value: 'pt-PT' },
  { name: 'Portuguese (Brazil)', value: 'pt-BR' },
  { name: 'Russian', value: 'ru-RU' },
  { name: 'Spanish (Spain)', value: 'es-ES' },
  { name: 'Spanish (United States)', value: 'es-US' },
  { name: 'Swedish', value: 'sv-SE' },
];

const newLanguageOptions = [
  { name: 'English (US)', value: 'en-US' },
];

const DEFAULT_EN_US_FEMALE = 'aura-2-thalia-en';
const DEFAULT_EN_US_MALE = 'aura-2-zeus-en';
const TELNYX_MALE = 'MAN';
const TELNYX_FEMALE = 'WOMAN';

export default {
  name: 'AudioComponent',

  components: {
    MultipleToggle,
    InputFile,
    InputNumber,
    InputText,
    InputTextarea,
    InputSelect,
    InputSearchableSelect,
    InputVoiceSelector,
    RecordingButton
  },

  props: {
    params: {
      type: Array,
      default() {
        return [];
      },
    },
    errors: {
      type: Array,
      default: null,
    },
    settings: {
      type: Object,
      default() {
        return null;
      },
    },
    parentSetting: {
      type: String,
      default: 'message',
    },
  },
  data() {
    return {
      legacyLanguageOptions: Object.freeze(legacyLanguageOptions),
      newLanguageOptions: Object.freeze(newLanguageOptions),
      sayInputOrder: Object.freeze(sayInputOrder)
    };
  },
  computed: {
    ...mapState({
      featureFlags: 'currentAccountFeatureFlags'
    }),
    ...mapState('assets', ['assetsList']),

    ...mapState('callFlows', [
      'availableVoices',
      'newTextToSpeechRecordingId',
      'newTextToSpeechRecordingUrl'
    ]),

    ...mapGetters('assets', ['getUploadAssetStatus']),

    ...mapGetters('callFlows', ['isLoading', 'convertTextToSpeechStatus']),

    settingsWatcher () {
      return Object.assign({}, this.settings);
    },

    recordingIsLoading() {
      return this.isLoading('convertTextToSpeech');
    },

    assetOptions() {
      return {
        options: this.assetsList,
        ...assetsConfig,
        placeholder: 'Select File',
      };
    },

    availableVoicesList() {
      return this.availableVoices.map((v) => {
        return {
          name: v.name,
          value: v.voiceId,
        };
      });
    },

    isLegacyVoiceSelected() {
      return this.settings[this.parentSetting].say.voice === TELNYX_FEMALE || this.settings[this.parentSetting].say.voice === TELNYX_MALE ; 
    },

    selectedWhisperType: {
      get() {
        // Return 'say' if text exists, otherwise 'play'
        return this.settings[this.parentSetting]?.say?.isEnabled ? 'say' : 'play';
      },
      set(value) {
        // Update the necessary settings when the value changes
        this.settings[this.parentSetting].say.isEnabled = value === 'say';
        this.settings[this.parentSetting].play.isEnabled = value === 'play';

        if (this.settings[this.parentSetting].say.isEnabled) {
          this.settings[this.parentSetting].play.URL = '';
        } else if (this.settings[this.parentSetting].play.isEnabled) {
          this.settings[this.parentSetting].say.text = '';
        }

        this.settings[this.parentSetting].isEnabled =
          this.settings[this.parentSetting].say.isEnabled ||
          this.settings[this.parentSetting].play.isEnabled;

        this.checkDefaultVoicePrompt();
        this.updateSettings();
      }
    },

    whisperTypeParameters() {
      return this.params.filter((p) => p.group === 'Message');
    },

    whisperTypeOptions() {
      return this.whisperTypeParameters.map((o) => {
        return {
          name: o.displayName,
          value: o.name,
        };
      });
    },

    ivrVoicesFeatureFlag() {
      return this.featureFlags.ivr_voices;
    },

    ivrGatherFeatureFlag() {
      return this.featureFlags.ivr_gather;
    },
  },
  watch: {
    settingsWatcher: {
      handler(newSettings, oldSettings) {
        // Handle voice response IVR toggle changed
        if (newSettings.voiceResponse !== oldSettings.voiceResponse) {
          // If voice response is toggled on:
          // - Switch to default voice: 
          //    - If Telnyx, Luna (female) or Zeus (male)
          //    - If Deepgram, default based on selected language (en-IE, en-GB, en-US)
          // - Disable Play functionality in AudioComponent; only Say is allowed for IVR
          if (newSettings.voiceResponse)
          {
            this.settings[this.parentSetting].say.isEnabled = true;
            this.settings[this.parentSetting].play.isEnabled = false;
            if (this.settings[this.parentSetting].say.voice === TELNYX_FEMALE) {
              this.settings[this.parentSetting].say.language = 'en-US';
              this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_FEMALE;
            } else if (this.settings[this.parentSetting].say.voice === TELNYX_MALE) {
              this.settings[this.parentSetting].say.language = 'en-US';
              this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_MALE;
            } else { // default case; reset to en-US since all languages are not supported in IVA
              this.settings[this.parentSetting].say.language = 'en-US';
              this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_FEMALE;
            }
            this.updateSettings();
          }
          // Voice Response toggled on and no text; fill in default prompt
          if (newSettings.voiceResponse && newSettings.prompt.say?.text === '') {
            this.checkDefaultVoicePrompt();
          }
        }

        // Handle Value to Capture changes; IF changed, fill in default prompt
        if (newSettings.valueToCapture !== oldSettings.valueToCapture) {
          this.checkDefaultVoicePrompt();
        }
        
      }
    }
  },
  async created() {
    this.settings[this.parentSetting].say.isEnabled =
      this.selectedWhisperType === 'say';
    this.settings[this.parentSetting].play.isEnabled =
      this.selectedWhisperType === 'play';
    this.settings[this.parentSetting].isEnabled =
      this.settings[this.parentSetting].say.isEnabled ||
      this.settings[this.parentSetting].play.isEnabled;

    // check if selected language is valid for selected voice
    // if not, default to legacy telnyx voice
    if (!this.isLegacyVoiceSelected) {
      if (!this.filterVoices().find((voice) => voice.voiceId === this.settings[this.parentSetting].say.voice)) {
        this.settings[this.parentSetting].say.voice = TELNYX_FEMALE;
        this.settings[this.parentSetting].say.textToSpeechRecordingId = '';
      }
    }
    
    this.updateSettings();

    // GET the text to speech recording url if the id is present in the settings object
    if (this.ivrVoicesFeatureFlag && this.selectedWhisperType === 'say' && this.settings[this.parentSetting].say.textToSpeechRecordingId ) {
      this.getTextToSpeechFile();
    }
  },
  mounted() {
    this.getAssets();
  },
  destroyed() {
    this.resetTextToSpeechState();
  },
  methods: {
    ...mapActions('assets', ['getAssets', 'uploadAsset']),

    ...mapActions('callFlows', ['convertTextToSpeech', 'resetTextToSpeechState']),

    getFieldError,
    getFieldSize,

    // check if default voice prompt needs to be set
    checkDefaultVoicePrompt() {
      // set default voice prompt if value to capture is ZIPCODE or AGE
      if (this.ivrGatherFeatureFlag && this.settings.voiceResponse) {
        if (this.settings.valueToCapture === 'ZIPCODE') {
          this.settings.prompt.say.text = this.$t('DEFAULT_PROMPT_ZIPCODE');
          this.handleVoiceTextChange();
        } else if (this.settings.valueToCapture === 'AGE') {
          this.settings.prompt.say.text = this.$t('DEFAULT_PROMPT_AGE');
          this.handleVoiceTextChange();
        } else if (this.settings.valueToCapture === 'PIN' || this.settings.valueToCapture === 'BOOLEAN') {
          this.settings.prompt.say.text = '';
          this.updateSettings();
        }
      }
    },

    // Leave legacy Telnyx voice as default, except for EN-IE; telnyx voices return as EN-US language, 
    // but they work for all of the ones in legacyLanguageOptions
    // If Voice Response is toggled, do not return legacy Telnyx voices as they don't work with IVR
    filterVoices() {
      return this.availableVoices.filter((voice) => {
        if (this.settings.voiceResponse) {
          return ((voice.language === this.settings[this.parentSetting].say.language
          && voice.voiceId !== TELNYX_MALE && voice.voiceId !== TELNYX_FEMALE));
        }
        // If no voice response toggle, can use any voice Telnyx or otherwise
        return ((voice.language === this.settings[this.parentSetting].say.language)
        || (voice.voiceId === TELNYX_MALE || voice.voiceId === TELNYX_FEMALE));
      });
    },

    whisperTypeChange(event) {
      // Simply set the computed property
      this.selectedWhisperType = event.value;
    },

    handleSettingsChange: debounce(function onSettingsChange() {
      this.updateSettings();
    }, 500),

    handleVoiceTextChange() {
      if (this.$refs?.recordingButton) {
        this.$refs?.recordingButton[0]?.hidePlayer();
      }
      this.settings[this.parentSetting].say.textToSpeechRecordingId = '';
      this.resetTextToSpeechState();
      this.updateSettings();

      if (!this.isLegacyVoiceSelected) {
        this.getTextToSpeechFile();
      }
    },

    handleLanguageChange(event) {
      //reset voice selector to default; voices are filtered by language
      if (this.settings.voiceResponse)
      {
        if (this.settings[this.parentSetting].say.voice === TELNYX_FEMALE) {
          this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_FEMALE;
        } else if (this.settings[this.parentSetting].say.voice === TELNYX_MALE) {
          this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_MALE;
        } else if (event === 'en-US') {
          this.settings[this.parentSetting].say.voice = DEFAULT_EN_US_FEMALE;
        }
      } else {
        this.settings[this.parentSetting].say.voice = TELNYX_FEMALE;
      }
      
      this.settings[this.parentSetting].say.textToSpeechRecordingId = '';
      this.resetTextToSpeechState();
      this.updateSettings();
      if (!this.isLegacyVoiceSelected) {
        this.getTextToSpeechFile();
      }
    },

    handleVoiceChange() {
      if (this.$refs?.recordingButton) {
        this.$refs?.recordingButton[0]?.hidePlayer();
      }
      this.settings[this.parentSetting].say.textToSpeechRecordingId = '';
      this.resetTextToSpeechState();
      this.updateSettings();

      if (!this.isLegacyVoiceSelected) {
        this.getTextToSpeechFile();
      }
    },

    updateSettings() {
      this.$emit('update:settings', this.settings);
    },

    async uploadAudioFile(file) {
      if (file) {
        // validate file mime type, can only be .mp3 or .wav
        if (file.type == 'audio/wav' || file.type == 'audio/mpeg') {
          const fileName = 'ivr-audio-' + Math.floor(Date.now() / 1000);
          const payload = {
            file: file,
            fileName: fileName,
            fileType: 'ivr-upload',
            isPublic: true,
            links: [],
          };

          const uploadedAsset = await this.uploadAsset(payload);

          if (this.getUploadAssetStatus.status === STATUS_ERROR) {
            utilities.showToaster(STATUS_ERROR, this.uploadAssetStatus.error);
          }
          if (this.getUploadAssetStatus.status === STATUS_SUCCESS) {
            utilities.showToaster(
              STATUS_SUCCESS,
              this.$t('DIAL_NODE_UPLOAD_SUCCESS')
            );
            this.settings[this.parentSetting].play.URL =
            uploadedAsset.fileAssets.publicUrl;
          }

          this.updateSettings();
        } else {
          utilities.showToaster('error',  this.$i18n.t('ERROR_FILE_EXTENSION'));
        }
      }
    },

    getTextToSpeechFile: debounce(async function getTextToSpeech() {
      const text = this.settings[this.parentSetting].say.text;
      const voiceId = this.settings[this.parentSetting].say.voice;
      if (text && voiceId) {
        await this.convertTextToSpeech({
          voiceId,
          providerId: 'deepgram',
          text
        });

        if (this.convertTextToSpeechStatus.status === STATUS_ERROR) {
          utilities.showToaster(STATUS_ERROR, this.convertTextToSpeechStatus.error);
        }
        if (this.convertTextToSpeechStatus.status === STATUS_SUCCESS) {
          this.settings[this.parentSetting].say.textToSpeechRecordingId =
            this.newTextToSpeechRecordingId;
        }

        this.updateSettings();
      }

    }, 1500),
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>